import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useNavigate } from 'react-router';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { appRoutes } from '@clearverify/constants';

import { useUserRegistrationByInviteMutation } from 'src/api';
import { Button, Link } from 'src/components/common';
import { AddressFields, PasswordField, PhoneField, TextField } from 'src/components/finalForm';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignup } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';

import { InitialStepFormFields, InitialStepFormValues } from '../types';
import { initialStepFormValidationSchema, prepareFormDataUser } from '../utils';

export const InitialStepForm: React.FC = () => {
  const navigate = useNavigate();

  const { inviteData, formStepperState, updateFormData, goToNextStep } = useAuthSignup();
  const isEmployee = inviteData?.role === BusinessRole.Employee;
  const { setAuthState } = useAppAuth();
  const { createApolloContext } = useNotification();

  const [signUpUserByInvite] = useUserRegistrationByInviteMutation({
    context: createApolloContext(),
  });

  const initialValues: Partial<InitialStepFormValues> = {
    email: inviteData?.email,
    isEmployee,
  };

  const handleSubmit = React.useCallback(
    async (formValues: InitialStepFormValues) => {
      // need these boolean flags since for PEO and PEOPP additionalStaff initial steps are not last steps

      if (formStepperState.isLastStep) {
        try {
          const { data } = await signUpUserByInvite({
            variables: await prepareFormDataUser(String(inviteData?.id), formValues),
          });
          await setAuthState({
            bearerToken: String(data?.userRegistrationByInvite.idToken),
            accessToken: data?.userRegistrationByInvite.accessToken ?? undefined,
          });
          navigate(appRoutes.auth.setupMfa);
        } catch (error) {
          console.error(error);
        }
      }

      updateFormData(formValues);
      goToNextStep();
    },
    [
      inviteData?.id,
      formStepperState.isLastStep,
      updateFormData,
      goToNextStep,
      signUpUserByInvite,
      setAuthState,
      navigate,
    ],
  );

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Sign up for Clear Verify
      </Typography>
      <Typography variant="body2" fontSize="16px" mt={1}>
        Already have an account? <Link to={appRoutes.auth.login}>Log in</Link>
      </Typography>
      <Form<InitialStepFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(initialStepFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid container spacing={1.5}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.firstName}
                    label="First Name"
                    slotProps={{ input: { autoComplete: 'given-name' } }}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.lastName}
                    label="Last Name"
                    slotProps={{ input: { autoComplete: 'family-name' } }}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.email}
                    label={isEmployee ? 'Personal Email' : 'Email'}
                    slotProps={{ input: { autoComplete: 'email' } }}
                    disabled
                    fullWidth
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PhoneField}
                    name={InitialStepFormFields.phoneNumber}
                    label="Phone Number"
                    slotProps={{ input: { autoComplete: 'off' } }}
                    fullWidth
                    required
                  />
                </Grid>

                {isEmployee && (
                  <Grid size={12}>
                    <AddressFields
                      prefix={InitialStepFormFields.homeAddress}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                )}

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PasswordField}
                    name={InitialStepFormFields.password}
                    label="Password"
                    slotProps={{ input: { autoComplete: 'new-password' } }}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PasswordField}
                    name={InitialStepFormFields.passwordConfirm}
                    label="Confirm Password"
                    slotProps={{ input: { autoComplete: 'off' } }}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
              <Box sx={visuallyHidden}>
                <Field component={TextField} name={InitialStepFormFields.isEmployee} />
              </Box>
              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  color="primary"
                  size="large"
                  variant="contained"
                >
                  Sign up
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
